import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Smartphone, Dock, Tablet, Zap, Code, Monitor, Database, Cloud } from "lucide-react";
import { RelatedServices } from "@/components/RelatedServices";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function CustomApps() {
  const { openDrawer } = useQuoteDrawer();

  const appTypes = [
    {
      title: "Mobile Applications",
      description: "Native and cross-platform mobile apps for iOS and Android with exceptional user experiences.",
      icon: <Smartphone className="h-8 w-8" />,
      features: ["Native iOS & Android", "Cross-platform", "Push Notifications", "Offline Functionality"]
    },
    {
      title: "Dock Applications",
      description: "Powerful desktop applications for Windows, macOS, and Linux with rich user interfaces.",
      icon: <Dock className="h-8 w-8" />,
      features: ["Cross-platform Dock", "Rich UI/UX", "System Integration", "High Performance"]
    },
    {
      title: "Web Applications",
      description: "Modern web applications with responsive design and progressive web app capabilities.",
      icon: <Tablet className="h-8 w-8" />,
      features: ["Progressive Web Apps", "Responsive Design", "Real-time Updates", "Cloud Integration"]
    },
    {
      title: "Enterprise Solutions",
      description: "Custom enterprise applications tailored to specific business processes and workflows.",
      icon: <Zap className="h-8 w-8" />,
      features: ["Business Process Automation", "Integration APIs", "Scalable Architecture", "Security Focused"]
    }
  ];

  const technologies = [
    { category: "Mobile", tools: ["React Native", "Flutter", "Swift", "Kotlin"] },
    { category: "Dock", tools: ["Electron", "Qt", "WPF", ".NET MAUI"] },
    { category: "Web", tools: ["React", "Vue.js", "Angular", "Node.js"] },
    { category: "Backend", tools: ["Python", "Java", "C#", "PostgreSQL"] }
  ];

  const industries = [
    { name: "Healthcare", icon: "fas fa-heartbeat", description: "Medical and healthcare applications" },
    { name: "Finance", icon: "fas fa-university", description: "Financial and banking solutions" },
    { name: "Education", icon: "fas fa-graduation-cap", description: "Educational and e-learning apps" },
    { name: "Retail", icon: "fas fa-store", description: "Retail and e-commerce applications" },
    { name: "Manufacturing", icon: "fas fa-industry", description: "Industrial and manufacturing apps" },
    { name: "Logistics", icon: "fas fa-shipping-fast", description: "Supply chain and logistics solutions" }
  ];

  return (
    <>
      <SEO 
        title="Custom Software Development UAE | ZeeXSoft"
        description="Tailored business software solutions in UAE. Build scalable, secure custom applications for your unique needs since 2017."
        keywords="custom software uae, business application development, enterprise solutions dubai"
      />

      <main>
    

     
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-br from-blue-50 via-gray-50 to-blue-50 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[url('/images/dot-pattern.svg')] bg-[size:40px_40px] opacity-5"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-blue-50/80"></div>
          </div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="lg:w-1/2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <motion.span 
                    className="inline-block px-4 py-2 text-sm font-medium rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white mb-6 shadow-lg"
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    Custom Software Development
                  </motion.span>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                      Tailored Business Solutions
                    </span>
                  </h1>
                  <p className="text-xl text-gray-600 mb-8 max-w-lg">
                    Custom applications designed specifically for your business needs and workflows
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Button 
                      onClick={openDrawer}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg shadow-lg"
                    >
                      Get a Free Quote
                    </Button>
                    <Button 
                      asChild
                      variant="outline"
                      className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-3 text-lg font-medium rounded-lg"
                    >
                      <Link href="/contact">
                        Contact Our Team
                      </Link>
                    </Button>
                  </div>
                </motion.div>
              </div>
              <div className="lg:w-1/2">
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="relative"
                >
                  <div className="relative w-full aspect-square max-w-lg mx-auto">
                    <div className="absolute inset-0 bg-white rounded-2xl shadow-xl border border-gray-200">
                      <div className="absolute top-4 left-4 right-4 h-8 bg-gray-100 rounded-t-lg flex items-center px-3 space-x-2">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      </div>
                      <div className="absolute inset-0 top-12 flex items-center justify-center">
                        <div className="text-center p-6">
                          <Code className="h-16 w-16 mx-auto text-blue-600 mb-4" />
                          <h3 className="text-xl font-medium text-gray-900 mb-2">Your Custom Application</h3>
                          <p className="text-gray-600">Tailored to your exact business requirements</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* App Types Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Types of Applications We Build</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Comprehensive solutions for every platform and business need
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {appTypes.map((app, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gray-50 p-8 rounded-xl border border-gray-200"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6 text-blue-600">
                    {app.icon}
                  </div>
                  <h3 className="font-orbitron font-semibold text-xl mb-4 text-gray-900">{app.title}</h3>
                  <p className="text-gray-600 mb-4">{app.description}</p>
                  <ul className="space-y-2">
                    {app.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Technologies We Use</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Modern, proven technologies to build robust applications
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {technologies.map((tech, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl shadow-sm border border-gray-200"
                >
                  <h3 className="font-orbitron font-semibold text-xl mb-6 text-gray-900">{tech.category}</h3>
                  <ul className="space-y-3">
                    {tech.tools.map((tool, i) => (
                      <li key={i} className="flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        <span className="text-gray-600">{tool}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Industries We Serve</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Custom solutions tailored to your industry's specific needs
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gray-50 p-6 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mr-4">
                      <i className={`${industry.icon} text-lg`}></i>
                    </div>
                    <h3 className="font-orbitron font-semibold text-lg text-gray-900">{industry.name}</h3>
                  </div>
                  <p className="text-gray-600">{industry.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Recent Projects</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Examples of custom applications we've developed
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Healthcare Management App",
                  description: "Custom mobile app for patient management and telemedicine",
                  platform: "Mobile App",
                  tech: ["React Native", "Node.js", "MongoDB"]
                },
                {
                  title: "Financial Dashboard",
                  description: "Dock application for financial reporting and analytics",
                  platform: "Dock App",
                  tech: ["Electron", "React", "PostgreSQL"]
                },
                {
                  title: "Inventory Management System",
                  description: "Web-based inventory tracking and management solution",
                  platform: "Web App",
                  tech: ["Vue.js", "Python", "Redis"]
                }
              ].map((project, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200"
                >
                  <div className="h-48 bg-blue-50 flex items-center justify-center">
                    <span className="text-blue-600">Project Showcase</span>
                  </div>
                  <div className="p-6">
                    <div className="mb-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded">{project.platform}</span>
                    </div>
                    <h3 className="font-orbitron font-semibold text-lg mb-2 text-gray-900">{project.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{project.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {project.tech.map((tech, techIndex) => (
                        <span key={techIndex} className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <RelatedServices 
              currentService="Custom Applications" 
              services={[
                {
                  title: "Mobile App Development",
                  description: "Build engaging mobile applications for iOS and Android platforms.",
                  icon: <Smartphone className="h-8 w-8" />
                },
                {
                  title: "Web Design & Development",
                  description: "Create responsive and interactive web applications for your business.",
                  icon: <Monitor className="h-8 w-8" />
                },
                {
                  title: "Cloud Services",
                  description: "Host and scale your custom applications in the cloud.",
                  icon: <Cloud className="h-8 w-8" />
                }
              ]} 
            />
          </div>
        </section>

        {/* Web Design Locations */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <WebDesignLocations />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-50">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">
                Ready to Build Your Custom App?
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Transform your ideas into powerful custom applications that drive business growth and efficiency
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={openDrawer}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg"
                >
                  Start Your Project
                </Button>
                <Link href="/contact">
                  <Button 
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-3 text-lg font-medium rounded-lg"
                  >
                    Discuss Your Idea
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
